import { fold } from "fp-ts/lib/Either";
import { pipe } from "fp-ts/lib/function";
import * as t from "io-ts";
import { IsJsonValue } from "../extensions/ts-toolbelt/IsJsonValue";

export function symmetric<A, O = A, I = O>(input: t.Type<A, O, unknown>): t.Type<A, O, I> {
  return new t.Type<A, O, I>(
    input.name,
    input.is,
    input.validate,
    input.encode
  );
}

// Basiert auf Code aus: https://stackoverflow.com/a/61311013
export type DecodePrismaInputObject<D, O> = (
  Omit<O, keyof D> & { [K in keyof D]:
    K extends keyof O
    ? DecodePrismaInputValue<D[K], O[K]>
    : D[K] }
) extends infer R ? { [K in keyof R]: R[K] } : never;

export type DecodePrismaInputJsonValue<R, O> =
  O extends null ? R
  : Exclude<R, null>;

type AreBothNullable<R, O> =
  null extends R
  ? null extends O
  ? 1
  : 0
  : 0;

export type DecodePrismaInputValue<R, O> =
  IsJsonValue<R> extends 1 ? DecodePrismaInputJsonValue<R, O>
  : AreBothNullable<R, O> extends 1 ? (DecodePrismaInputValue<Exclude<R, null>, Exclude<O, null>> | null)
  : [R, O] extends [string, string] ? string
  : [R, O] extends [boolean, boolean] ? boolean
  : [R, O] extends [number, number] ? number
  : [R, O] extends [Date, Date] ? Date
  : [R, O] extends [Array<infer RI>, Array<infer OI>] ? Array<DecodePrismaInputValue<RI, OI>>
  : [R, O] extends [object, object] ? DecodePrismaInputObject<R, O>
  : O;

/**
 * Prüft, ob die Abfrage vollständig und ob die Typen der Datenbank mit denen aus dem Codec übereinstimmen.
 * @param codec Der Codec welcher den Wert zum richtigen Typ umwandelt.
 * @param value Der Wert der zum richtigen Typ umgewandelt werden. Achtung: Sind hier zu viele Eigenschaften, gibt es kein Fehler!
 * @returns Gibt den dekodierten Wert zurück. Enthält meistens auch die zusätzlichen Eigenschaften.
 */
export function decodePrisma<R, A, O>(codec: t.Type<A, O, unknown>, value: DecodePrismaInputValue<R, O>): t.Validation<A> {
  return codec.decode(value);
}

declare type LiteralValue = string | number | boolean

export const literalUnion = <U extends LiteralValue>() => {
  return t.literal as <V extends U>(value: V, name?: string | undefined) => t.LiteralC<V>;
}

export function forInterface<U>() {
  return <A extends U, O, I>(codec: t.Type<A, O, I>, _checker: ((codec: U) => A)) => {
    return codec as unknown as t.Type<U extends A ? U : A, O, I>;
  };
}

export function nullIfNot<A, O, I>(codec: t.Type<A, O, I>, value: I): A | null {
  return pipe(
    codec.decode(value),
    fold(
      _ => null,
      parsed => parsed,
    )
  );
}
